import React, { useMemo, useState } from "react";

// Components
import DeleteDocumentModal from "./components/DeleteDocumentModal";
import { DownloadAllMyFiles, File, Folder, TotalSize } from "./components/DocumentTableRows";
import EditDocumentModal from "./components/EditDocumentModal";
import ScanWarningModal from "./components/ScanWarningModal";
import ViewDocumentModal from "./components/ViewDocumentModal";

// hooks
import { useDeleteDocument, useEditDocument, useUpdateDocumentVirusScanStatus } from "../../api/hooks/defs/documents";
import useToast from "../../hooks/useToast";

// helpers
import { buildRows, useIsCompanyDocumentPage } from "./helpers";
// schema
import {
	DOWNLOAD_ALL_MY_FILES,
	type DocumentFile,
	type DocumentTableRow,
	type DocumentVisibility,
	type DocumentsData,
	FILE,
	FOLDER,
	type FolderState,
	TOTAL_SIZE,
	type UserData,
} from "./schema";

// Map row type to component
const mapping = {
	[FOLDER]: Folder,
	[TOTAL_SIZE]: TotalSize,
	[DOWNLOAD_ALL_MY_FILES]: DownloadAllMyFiles,
	[FILE]: File,
};

const columns = [
	{
		id: "FileName",
		label: "File Name",
		isSortable: false,
		getter: (r) => mapping[r.type](r),
	},
];

// use document table hook to handle all the logic for the document table, including the modals
const useDocumentTable = (documents: DocumentsData, userData: UserData, setDocuments) => {
	const isCompanyDocumentPage = useIsCompanyDocumentPage();

	// default state of folders depending on the nav query params.
	const [folderState, setFolderState] = useState<FolderState>({
		companyDocumentsOpen: isCompanyDocumentPage,
		signedDocumentsOpen: false,
		myDocumentsOpen: !isCompanyDocumentPage,
	});
	const [deleteModalShow, setDeleteModalShow] = useState(false);
	const [editModalShow, setEditModalShow] = useState(false);
	const [viewModalShow, setViewModalShow] = useState(false);
	const [scanWarningModalShow, setScanWarningModalShow] = useState(false);
	const [selectedDocument, setSelectedDocument] = useState<DocumentFile | null>(null);

	const { mutateAsync: deleteDocumentAsync } = useDeleteDocument();
	const { mutateAsync: editDocumentAsync } = useEditDocument();
	const { mutateAsync: updateDocumentVirusScanStatus } = useUpdateDocumentVirusScanStatus();

	const toast = useToast();

	// can be optimized if we save documents in a map documents[folderName][id] = document.
	const deleteDocument = async () => {
		const id = selectedDocument?.id;
		const newDocuments = { ...documents };
		setDeleteModalShow(false);
		if (id) {
			try {
				await deleteDocumentAsync(id);

				for (const key of Object.keys(newDocuments)) {
					if (Array.isArray(newDocuments[key])) {
						newDocuments[key] = newDocuments[key].filter((f) => f._id !== id);
					}
				}

				setDocuments(newDocuments);
				toast("Document deleted", {
					type: "success",
					duration: 5000,
					size: "small",
				});
			} catch (e) {
				toast("Error deleting document", {
					type: "error",
					duration: 5000,
					size: "small",
				});
			}
		}
	};

	const editDocument = async (
		id: string,
		fileName: string,
		docVisibilities: DocumentVisibility[],
		tags: string[],
		allowAiRetrieval?: boolean
	) => {
		try {
			setEditModalShow(false);
			// For company documents: true = Everyone, false = Administrators Only
			const vis_to_everyone = docVisibilities.some((v) => v.value === "vis_to_everyone");
			// For user documents: checkboxes
			const vis_to_emp = docVisibilities.some((v) => v.value === "vis_to_emp");
			const vis_to_man = docVisibilities.some((v) => v.value === "vis_to_m");
			await editDocumentAsync({
				id,
				fileName,
				vis_to_everyone,
				vis_to_man,
				vis_to_emp,
				tags,
				allow_ai_retrieval: allowAiRetrieval,
			});
			const newDocuments = Object.assign({}, documents);

			for (const key of Object.keys(newDocuments)) {
				if (Array.isArray(newDocuments[key])) {
					newDocuments[key] = newDocuments[key].map((f) => {
						if (f._id === id) {
							f.filename = fileName;
							f.tags = tags;
							// if document has vis_to_everyone field, it means it's a company document
							if (f.vis_to_everyone !== undefined) {
								f.vis_to_everyone = vis_to_everyone;
							} else {
								f.vis_to_emp = vis_to_emp;
								f.vis_to_man = vis_to_man;
							}
							if (allowAiRetrieval !== undefined) {
								f.allow_ai_retrieval = allowAiRetrieval;
							}
						}
						return f;
					});
				}
			}

			toast("Edited document", {
				type: "success",
				duration: 5000,
				size: "small",
			});
			setDocuments(newDocuments);
		} catch (e) {
			toast("Error editing document", {
				type: "error",
				duration: 5000,
				size: "small",
			});
			throw e;
		}
	};

	const toggleFolderState = (key: keyof FolderState) => {
		setFolderState((state) => ({ ...state, [key]: !state[key] }));
	};

	const handleSelectEdit = (document: DocumentFile) => {
		setSelectedDocument(document);
		setEditModalShow(true);
	};

	const handleSelectDelete = (document: DocumentFile) => {
		setSelectedDocument(document);
		setDeleteModalShow(true);
	};

	const checkVirusScanStatus = async (
		document: DocumentFile
	): Promise<{
		clean: boolean;
		pending?: boolean;
	}> => {
		// if the document is already clean or unsafe, and pending is true, we don't need to check the virus scan status
		const shouldCheckVirusScanStatus =
			!(document.clean === undefined || document.clean === null) && document.pending === false;

		if (shouldCheckVirusScanStatus) {
			return {
				clean: document.clean,
				pending: document.pending,
			};
		}

		let clean: boolean = document.clean;
		let pending: boolean = document.pending ?? false;

		try {
			// otherwise, check the virus scan results
			const { virusScanStatus } = await updateDocumentVirusScanStatus(document.id);

			switch (virusScanStatus) {
				// document was deemed clean
				case "clean":
					clean = true;
					pending = false;
					break;
				// document was deemed unsafe
				case "unsafe":
					clean = false;
					pending = false;
					break;
				// no tags were found, so we assume the scan is still pending
				case "no-tag":
					clean = false;
					pending = true;
					break;
				default:
					clean = false;
					pending = true;
					break;
			}

			return {
				clean,
				pending,
			};
			// if the call to the virus scan status endpoint fails, we'll mark the document as pending and not clean
			// the user will have the option to view the document, but will be warned that we can't guarantee it's safe
		} catch {
			clean = false;
			pending = true;
		}

		return {
			clean,
			pending,
		};
	};

	const handleViewDocument = async (document: DocumentFile) => {
		setSelectedDocument(document);

		const { clean, pending } = await checkVirusScanStatus(document);

		// update the document here with the results of the virus scan status check
		// this is necessary for displaying the correct status driven UI elements
		document.clean = clean;
		document.pending = pending;

		const isDocumentClean = clean && !pending;

		if (!isDocumentClean) {
			setScanWarningModalShow(true);
		} else {
			setViewModalShow(true);
		}
	};

	const handleWarningCancel = () => {
		setSelectedDocument(null);
		setScanWarningModalShow(false);
	};

	const handleToggleAiRetrieval = async (document: DocumentFile, newValue: boolean) => {
		try {
			await editDocumentAsync({
				id: document._id,
				fileName: document.filename,
				vis_to_everyone: document.vis_to_everyone,
				vis_to_man: document.vis_to_man,
				vis_to_emp: document.vis_to_emp,
				tags: document.tags || [],
				allow_ai_retrieval: newValue,
			});

			const newDocuments = Object.assign({}, documents);
			for (const key of Object.keys(newDocuments)) {
				if (Array.isArray(newDocuments[key])) {
					newDocuments[key] = newDocuments[key].map((f) => {
						if (f._id === document._id) {
							f.allow_ai_retrieval = newValue;
						}
						return f;
					});
				}
			}

			setDocuments(newDocuments);
			toast(newValue ? "Document Enabled for Ask PerformYard." : "Document Disabled for Ask PerformYard.", {
				type: "success",
				duration: 5000,
				size: "small",
			});
		} catch (e) {
			toast("Error updating Ask PerformYard setting", {
				type: "error",
				duration: 5000,
				size: "small",
			});
		}
	};

	const buildRowsProps = {
		documents,
		folderState,
		userData,
		toggleFolderState,
		handleSelectEdit,
		handleSelectDelete,
		handleViewDocument,
		handleToggleAiRetrieval,
		isCompanyDocumentPage,
	};

	const rows: DocumentTableRow[] = useMemo(
		() => buildRows(buildRowsProps),
		[buildRows, folderState, documents, userData]
	);

	const ViewDocumentModalComponent = (
		<ViewDocumentModal
			show={viewModalShow}
			onClose={() => setViewModalShow(false)}
			userData={userData}
			document={selectedDocument}
		/>
	);
	const ScanWarningModalComponent = <ScanWarningModal show={scanWarningModalShow} onClose={handleWarningCancel} />;
	const DeleteDocumentModalComponent = (
		<DeleteDocumentModal
			show={deleteModalShow}
			onClose={() => setDeleteModalShow(false)}
			onDelete={deleteDocument}
		/>
	);
	const EditDocumentModalComponent = (
		<EditDocumentModal
			show={editModalShow}
			onClose={() => setEditModalShow(false)}
			userData={userData}
			document={selectedDocument}
			onEdit={editDocument}
		/>
	);

	return {
		columns,
		rows,
		ViewDocumentModalComponent,
		ScanWarningModalComponent,
		DeleteDocumentModalComponent,
		EditDocumentModalComponent,
	};
};

export default useDocumentTable;
